import { getAuthHeaders } from "../lib/auth";
import { useState, useEffect } from "react";
import LogLayout from "../components/LogLayout";
import axios from "axios";
import { toast } from "sonner";
import { Book, Code, FileText, Shield, ChevronRight, Server, Database, Globe, Lock, Zap, Download, RefreshCw, Loader2, Wallet, Users, TrendingUp, BarChart3, Truck, Archive, AlertTriangle, FileSpreadsheet, Building2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;

const SECTIONS = [
  { id: "guide", label: "Guide d'utilisation", icon: Book, docId: "guide-utilisateur" },
  { id: "features", label: "Fonctionnalites", icon: Zap, docId: "fonctionnalites" },
  { id: "technical", label: "Documentation technique", icon: Server, docId: "doc-technique" },
  { id: "api", label: "API & Endpoints", icon: Code, docId: "doc-technique" },
];

const FEATURES_DATA = [
  { module: "CMES-LOG : Facturation & Logistique", icon: FileText, color: "blue", items: [
    { name: "Dossiers Factures", desc: "Création, scan OCR IA, correction manuelle, statuts (Éditée → Déposée → Paiement partiel → Payée), historique complet" },
    { name: "Pipeline Kanban", desc: "6 étapes synchronisées automatiquement : À déposer, Déposée, Chèque à récupérer, Bordereau, Remise chèque, Encaissé" },
    { name: "Suivi Paiements par Assureur", desc: "Montants, payé, encours, taux, délai, en retard (>45j), critique (>90j). Détail par facture avec badge statut." },
    { name: "Créances & Trésorerie", desc: "Créances par assureur, plafonds, trésorerie prévisionnelle, ratio créances/CA, encaissements prévus" },
    { name: "Expéditions & Courses", desc: "Pool agents commerciaux, assignation auto, suivi GPS temps réel, bordereaux, code retour QR" },
    { name: "Rapports & Analytics IA", desc: "Rapports mensuels PDF, score santé /100, Dashboard IA prédictif, benchmarking marché" },
    { name: "Alertes Intelligentes", desc: "Détection retards automatique, niveaux Normal/En retard/Critique, relance en un clic" },
    { name: "Archives & API Caisse", desc: "Archives universelles, API POS/LGO, export CSV/Excel/PDF, portail B2B Assurances" },
  ]},
  { module: "CMES INTEL-STOCK : Stocks & Périmés", icon: Shield, color: "emerald", items: [
    { name: "Gestion des produits", desc: "Inventaire complet, import CSV/Excel/PDF, scan IA photo/PDF, catégories" },
    { name: "Alertes Péremption", desc: "4 niveaux (1, 2, 3, 6 mois + expirés), KPIs, recommandations IA" },
    { name: "Détection Invendus", desc: "Produits sans vente >90 jours, seuil configurable, recommandations d'action" },
    { name: "Auto-Destruction", desc: "CRON quotidien automatique, seuil configurable, historique" },
    { name: "Scanner IA de Produits", desc: "Photo/PDF → OCR GPT-4o → extraction, enrichissement IA, validation" },
    { name: "Destructions Sécurisées", desc: "PV certifié PDF + QR Code, attestation officielle, suivi tonnage, carte CI" },
    { name: "Indicateurs ITPP & Sentinel", desc: "Conseils, indicateurs, PharmExchange, suivi mensuel" },
  ]},
  { module: "CMES TECH-CONSEIL : Intelligence Pharmaceutique IA", icon: TrendingUp, color: "violet", items: [
    { name: "Scanner d'Ordonnances", desc: "OCR IA (manuscrit/imprimé/PDF), 3 modes : Classique, Assurance, Vérification Rapide au comptoir" },
    { name: "Analyse Clinique", desc: "Interactions (Grave/Modérée/Faible), doublons, surdosage, contre-indications" },
    { name: "Posologie Intelligente", desc: "Calcul adapté au poids/âge (pédiatrie), sources VIDAL/Dorosz/ANSM/Lexicomp/Micromedex" },
    { name: "Alertes Cliniques", desc: "Grossesse, allaitement, insuffisance rénale/hépatique — détection automatique" },
    { name: "Conseils Patient", desc: "Ce que le pharmacien doit dire pour chaque médicament" },
    { name: "Base Médicaments", desc: "40+ médicaments ivoiriens, CRUD admin, autocomplétion, 21+ interactions" },
    { name: "Copilot IA & Digital Twin", desc: "Assistant IA, profil patient virtuel, Google du Médicament Africain" },
    { name: "Pharmacovigilance IA", desc: "Rappels de lots, retraits, ruptures, score qualité officine" },
  ]},
  { module: "Panel SuperAdmin", icon: Users, color: "amber", items: [
    { name: "Suivi Paiements", desc: "Tableau clients, ventilation mensuelle, graphique d'évolution, PDF/CSV" },
    { name: "Comparatif Pharmacies", desc: "Classement par taux de recouvrement, objectifs, écarts" },
    { name: "Gestion des Comptes", desc: "Pharmacies, établissements de santé, agents commerciaux, admins. Approbation/suspension" },
    { name: "QR Code Inscription", desc: "QR code imprimable pour l'inscription directe des clients" },
    { name: "Manuel Client PDF", desc: "Génération du manuel d'utilisation imprimable (7 pages)" },
    { name: "Documentation Technique", desc: "APIs, modules, endpoints, guide complet" },
  ]},
  { module: "Portail Assurances (B2B)", icon: Building2, color: "cyan", items: [
    { name: "Dashboard assureur", desc: "Consultation des factures, actions valider/rejeter" },
    { name: "Messagerie sécurisée", desc: "Communication par thread sur chaque facture" },
  ]},
];

const API_ENDPOINTS = [
  { method: "POST", path: "/api/auth/login", desc: "Authentification" },
  { method: "POST", path: "/api/auth/register", desc: "Inscription (pharmacie, établissement, agent commercial)" },
  { method: "GET", path: "/api/auth/me", desc: "Profil utilisateur" },
  { method: "GET", path: "/api/log/invoices", desc: "Liste des factures" },
  { method: "POST", path: "/api/log/invoices", desc: "Créer une facture" },
  { method: "GET", path: "/api/log/invoices/{id}", desc: "Détail facture" },
  { method: "PUT", path: "/api/log/invoices/{id}/status", desc: "Changer statut (synchro pipeline auto)" },
  { method: "PUT", path: "/api/log/invoices/{id}/confirm-depot", desc: "Confirmer dépôt" },
  { method: "PUT", path: "/api/log/invoices/{id}/reject", desc: "Rejeter facture" },
  { method: "POST", path: "/api/log/invoices/{id}/cash-payment", desc: "Paiement espèces" },
  { method: "PUT", path: "/api/log/invoices/{id}/complete-ocr", desc: "Correction OCR" },
  { method: "POST", path: "/api/log/invoices/auto-upload", desc: "Upload-Auto OCR" },
  { method: "GET", path: "/api/log/invoices/export-csv", desc: "Export CSV" },
  { method: "GET", path: "/api/log/invoices/insurers", desc: "Liste assureurs (déchiffrée)" },
  { method: "GET", path: "/api/log/pipeline/dashboard", desc: "KPIs Pipeline" },
  { method: "GET", path: "/api/log/pipeline/invoices", desc: "Factures Pipeline (statut + stage)" },
  { method: "GET", path: "/api/log/pipeline/payment-tracking", desc: "Suivi paiements par assureur" },
  { method: "GET", path: "/api/log/pipeline/payment-tracking/pdf", desc: "Export PDF suivi paiements" },
  { method: "GET", path: "/api/log/receivables/summary", desc: "Résumé créances" },
  { method: "GET", path: "/api/log/receivables/monthly-evolution", desc: "Évolution mensuelle créances" },
  { method: "GET", path: "/api/log/receivables/treasury-impact", desc: "Impact trésorerie" },
  { method: "GET", path: "/api/log/receivables/export-pdf", desc: "Export PDF créances" },
  { method: "GET", path: "/api/log/insurer-rankings", desc: "Classement IA des assureurs" },
  { method: "POST", path: "/api/prescription-scanner/scan", desc: "Scanner ordonnance (OCR + analyse clinique)" },
  { method: "POST", path: "/api/prescription-scanner/quick-check", desc: "Vérification rapide médicaments" },
  { method: "GET", path: "/api/prescription-scanner/medications/search", desc: "Autocomplétion médicaments" },
  { method: "GET", path: "/api/prescription-scanner/medications", desc: "Base médicaments complète" },
  { method: "POST", path: "/api/prescription-scanner/medications", desc: "Ajouter médicament (admin)" },
  { method: "GET", path: "/api/prescription-scanner/insurances", desc: "Assurances ordonnances" },
  { method: "GET", path: "/api/prescription-scanner/history", desc: "Historique scans" },
  { method: "GET", path: "/api/stock-intelligence/alerts", desc: "Alertes péremption/invendus" },
  { method: "POST", path: "/api/stock-intelligence/auto-destroy/trigger", desc: "Déclencher auto-destruction" },
  { method: "GET", path: "/api/products", desc: "Liste produits" },
  { method: "POST", path: "/api/destructions", desc: "Demande de destruction" },
  { method: "GET", path: "/api/subscription-plans/plans", desc: "Formules d'abonnement" },
  { method: "POST", path: "/api/subscription-plans/select", desc: "Choisir un abonnement" },
  { method: "GET", path: "/api/log/documentation/client-manual/pdf", desc: "Manuel client PDF" },
  { method: "GET", path: "/api/superadmin/accounts", desc: "Comptes utilisateurs" },
  { method: "POST", path: "/api/superadmin/create-account", desc: "Créer compte" },
];

const METHOD_COLORS = {
  GET: "bg-emerald-500/15 text-emerald-400",
  POST: "bg-blue-500/15 text-blue-400",
  PUT: "bg-amber-500/15 text-amber-400",
  DELETE: "bg-red-500/15 text-red-400",
};

const GUIDE_STEPS = [
  { title: "1. Connexion", desc: "Connectez-vous avec vos identifiants. Session auto-expire après 10 min d'inactivité. Page Hub pour choisir votre service (LOG, INTEL-STOCK, TECH-CONSEIL).", icon: Lock },
  { title: "2. Dossiers Factures", desc: "Créez un dossier pour chaque facture. Uploadez les documents. L'OCR extrait les informations automatiquement. Corrigez manuellement si nécessaire.", icon: FileText },
  { title: "3. Pipeline", desc: "Suivez vos factures en Kanban : À déposer → Déposée → Chèque à récupérer → Bordereau → Remise chèque → Encaissé. Synchronisation automatique des statuts.", icon: BarChart3 },
  { title: "4. Paiements", desc: "Enregistrez des paiements partiels ou totaux. Chaque paiement est tracé. Le statut et la position dans le pipeline se mettent à jour automatiquement.", icon: Wallet },
  { title: "5. Créances & Trésorerie", desc: "Vue consolidée par assureur avec plafonds configurables. Trésorerie prévisionnelle. Graphiques d'évolution mensuelle.", icon: TrendingUp },
  { title: "6. Expéditions", desc: "Créez des courses. Les agents commerciaux les prennent en charge. Suivi GPS temps réel. Code QR de retour à la complétion.", icon: Truck },
  { title: "7. Scanner Ordonnances", desc: "Scannez une ordonnance → analyse clinique IA : interactions, posologie, alertes grossesse/rein/foie, conseils patient. Mode Vérification Rapide au comptoir.", icon: Shield },
  { title: "8. Alertes Stock", desc: "Péremptions à 1/2/3/6 mois, invendus >90j, auto-destruction programmée. Scanner IA de produits (photo → extraction).", icon: AlertTriangle },
  { title: "9. Rapports & Analytics", desc: "Rapports mensuels IA. Score santé /100. Dashboard IA prédictif. Benchmarking marché par ville, commune, assureur.", icon: FileSpreadsheet },
  { title: "10. SuperAdmin", desc: "Suivi paiements mensuels (PDF/CSV). Comparatif inter-pharmacies. QR Code inscription. Manuel client PDF.", icon: Users },
];

export default function LogDocumentationPage() {
  const [activeSection, setActiveSection] = useState("guide");
  const [refreshing, setRefreshing] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/log/documentation/refresh`, {}, { headers: getHeaders() });
      toast.success("Documentation actualisee avec succes");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'actualisation");
    } finally { setRefreshing(false); }
  };

  const handleDownloadPdf = async (docId, title) => {
    setDownloading(docId);
    try {
      // First refresh to ensure latest content
      try { await axios.post(`${API}/log/documentation/refresh`, {}, { headers: getHeaders() }); } catch {}
      const res = await axios.get(`${API}/log/documentation/${docId}/pdf`, {
        headers: getHeaders(), responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `CMES_${title.replace(/\s+/g, "_")}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${title} telecharge en PDF`);
    } catch (e) {
      toast.error("Erreur de telechargement");
    } finally { setDownloading(null); }
  };

  const handleDownloadManual = async () => {
    setDownloading("client-manual");
    try {
      const res = await axios.get(`${API}/log/documentation/client-manual/pdf`, {
        headers: getHeaders(), responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "CMES-LOG_Manuel_Client_Pharmacien.pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Manuel client téléchargé !");
    } catch (e) {
      toast.error("Erreur de téléchargement");
    } finally { setDownloading(null); }
  };

  return (
    <LogLayout title="Documentation" subtitle="Guides, fonctionnalités et documentation technique">
      <div data-testid="log-documentation-page" className="space-y-6">
        {/* Tabs + Actions */}
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          <div className="flex gap-1.5 bg-slate-900/60 p-1 rounded-xl border border-white/5 w-fit flex-wrap">
            {SECTIONS.map(s => { const Icon = s.icon; return (
              <button key={s.id} onClick={() => setActiveSection(s.id)} data-testid={`doc-tab-${s.id}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all ${activeSection === s.id ? "bg-blue-600 text-white font-medium" : "text-slate-400 hover:text-white hover:bg-white/5"}`}>
                <Icon className="w-4 h-4" /> {s.label}
              </button>
            ); })}
          </div>
          <div className="flex gap-2">
            <Button onClick={handleRefresh} disabled={refreshing}
              className="bg-slate-800 hover:bg-slate-700 text-white gap-2 text-xs" data-testid="doc-refresh-btn">
              {refreshing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Actualiser
            </Button>
          </div>
        </div>

        {/* Download Bar */}
        <div className="bg-gradient-to-r from-blue-600/10 to-cyan-600/10 border border-blue-500/20 rounded-xl p-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <Download className="w-4 h-4 text-blue-400" /> Télécharger la documentation
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">Exportez chaque document en PDF pour impression ou partage</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button onClick={handleDownloadManual}
                disabled={downloading === "client-manual"}
                className="bg-violet-600 hover:bg-violet-700 text-white gap-2 text-xs ring-2 ring-violet-400/30" data-testid="download-client-manual">
                {downloading === "client-manual" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                Manuel Client Pharmacien
              </Button>
              {[
                { id: "guide-utilisateur", label: "Guide Utilisateur", color: "bg-blue-600 hover:bg-blue-700" },
                { id: "fonctionnalites", label: "Fonctionnalités", color: "bg-amber-600 hover:bg-amber-700" },
                { id: "doc-technique", label: "Doc Technique", color: "bg-emerald-600 hover:bg-emerald-700" },
              ].map(doc => (
                <Button key={doc.id} onClick={() => handleDownloadPdf(doc.id, doc.label)}
                  disabled={downloading === doc.id}
                  className={`${doc.color} text-white gap-2 text-xs`} data-testid={`download-${doc.id}`}>
                  {downloading === doc.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                  {doc.label}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* Guide Tab */}
        {activeSection === "guide" && (
          <div className="space-y-4">
            <Card className="bg-slate-900/60 border-white/5">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Book className="w-5 h-5 text-blue-400" /> Guide d'utilisation CMES Pharma Intel
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {GUIDE_STEPS.map(step => {
                  const Icon = step.icon;
                  return (
                    <div key={step.title} className="flex items-start gap-3 p-3 rounded-lg bg-slate-800/40 border border-white/5 hover:bg-slate-800/60 transition-colors">
                      <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Icon className="w-4 h-4 text-blue-400" />
                      </div>
                      <div>
                        <h4 className="text-white font-semibold text-sm">{step.title}</h4>
                        <p className="text-slate-400 text-xs leading-relaxed mt-0.5">{step.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Features Tab */}
        {activeSection === "features" && FEATURES_DATA.map(mod => (
          <Card key={mod.module} className="bg-slate-900/60 border-white/5">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <mod.icon className={`w-5 h-5 text-${mod.color}-400`} /> {mod.module}
              </CardTitle>
            </CardHeader>
            <CardContent><div className="space-y-1.5">
              {mod.items.map(item => (
                <div key={item.name} className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-white/[0.02] transition-colors">
                  <ChevronRight className={`w-4 h-4 text-${mod.color}-400 mt-0.5 flex-shrink-0`} />
                  <div>
                    <p className="text-sm text-white font-medium">{item.name}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div></CardContent>
          </Card>
        ))}

        {/* Technical Tab */}
        {activeSection === "technical" && (
          <div className="space-y-4">
            <Card className="bg-slate-900/60 border-white/5">
              <CardHeader><CardTitle className="text-white flex items-center gap-2"><Server className="w-5 h-5 text-emerald-400" /> Architecture</CardTitle></CardHeader>
              <CardContent className="space-y-2 text-sm">
                {[
                  [Globe, "Frontend", "React 18 + Tailwind CSS + Shadcn/UI + Recharts + React-Leaflet"],
                  [Server, "Backend", "Python FastAPI + Motor (MongoDB async) + APScheduler"],
                  [Database, "Base de donnees", "MongoDB (Motor async driver)"],
                  [Lock, "Securite", "JWT (HS256) + bcrypt + HSTS + CSP + Rate Limiting + Timeout 10min"],
                  [Brain, "IA & OCR", "OpenAI GPT Vision + scikit-learn (via emergentintegrations)"],
                  [FileText, "Emails", "Resend (notifications@cmes-pharma.org)"],
                  [Wallet, "Paiements", "Stripe (mode test) + Especes manuelles"],
                  [Archive, "Stockage", "Emergent Object Storage + reportlab (PDF) + qrcode"],
                ].map(([Icon, l, v]) => (
                  <div key={l} className="flex items-start gap-3 p-2.5 rounded-lg bg-slate-800/30">
                    <Icon className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                    <div><span className="text-white font-medium text-xs">{l} :</span><span className="text-slate-400 text-xs ml-1">{v}</span></div>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card className="bg-slate-900/60 border-white/5">
              <CardHeader><CardTitle className="text-white flex items-center gap-2"><Lock className="w-5 h-5 text-red-400" /> Securite</CardTitle></CardHeader>
              <CardContent className="text-sm text-slate-300 space-y-1.5">
                <p className="text-slate-400">- JWT avec expiration + Mots de passe bcrypt</p>
                <p className="text-slate-400">- Headers : HSTS, X-Frame-Options, CSP, Permissions-Policy, X-Content-Type-Options</p>
                <p className="text-slate-400">- Rate limiting + Timeout session 10 minutes</p>
                <p className="text-slate-400">- Audit trail complet + Roles granulaires (5 roles)</p>
                <p className="text-slate-400">- Middleware de securite FastAPI</p>
              </CardContent>
            </Card>
            <Card className="bg-slate-900/60 border-white/5">
              <CardHeader><CardTitle className="text-white flex items-center gap-2"><Users className="w-5 h-5 text-amber-400" /> Roles et Permissions</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-1.5">
                  {[
                    { role: "superadmin", desc: "Acces total, gestion utilisateurs, comparatifs, suivi paiements global", color: "amber" },
                    { role: "admin", desc: "Acces aux factures de toutes les pharmacies, supervision agents commerciaux", color: "cyan" },
                    { role: "pharmacy", desc: "Gestion de sa pharmacie, factures, produits, destructions", color: "blue" },
                    { role: "agent commercial", desc: "Interface dediee, pool de courses, auto-attribution", color: "emerald" },
                    { role: "assurance", desc: "Portail B2B, consultation factures assignees", color: "violet" },
                  ].map(r => (
                    <div key={r.role} className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-800/30">
                      <span className={`px-2.5 py-1 rounded-lg text-xs font-bold bg-${r.color}-500/15 text-${r.color}-400`}>{r.role}</span>
                      <span className="text-xs text-slate-400">{r.desc}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* API Tab */}
        {activeSection === "api" && (
          <Card className="bg-slate-900/60 border-white/5">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Code className="w-5 h-5 text-violet-400" /> Endpoints API ({API_ENDPOINTS.length})
              </CardTitle>
            </CardHeader>
            <CardContent><div className="space-y-1">
              {API_ENDPOINTS.map((ep, i) => (
                <div key={i} className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/[0.02] font-mono transition-colors">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold min-w-[40px] text-center ${METHOD_COLORS[ep.method]}`}>{ep.method}</span>
                  <span className="text-xs text-blue-400 flex-1">{ep.path}</span>
                  <span className="text-[11px] text-slate-500">{ep.desc}</span>
                </div>
              ))}
            </div></CardContent>
          </Card>
        )}
      </div>
    </LogLayout>
  );
}
